/**
 * Input schemas: the shape of what the UI or API sends to a command.
 * Money is integer paisa, quantities integer milli-units (see money.ts).
 */
import { z } from 'zod';
import { MAX_AMOUNT, MAX_QTY, type Milli, type Paisa } from './money.js';
import {
  ACCOUNT_KINDS,
  ADJUST_REASONS,
  BINS,
  PARTY_TYPES,
  RETURN_REASONS,
  UNITS,
} from './types.js';

function isCalendarDate(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 2000 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= days;
}

export const isoDate = z
  .string()
  .refine(isCalendarDate, { message: 'Enter a valid date (YYYY-MM-DD).' });

export const id = z.string().trim().min(1, 'Required.').max(64);
export const note = z.string().trim().max(500, 'Note is too long.').default('');
export const label = z.string().trim().min(1, 'Required.').max(100, 'Too long.');

/** Non-negative paisa. */
export const amount = z
  .number()
  .int('Amount must be whole paisa.')
  .min(0, 'Amount cannot be negative.')
  .max(MAX_AMOUNT, 'Amount is too large.');
export const positiveAmount = amount.min(1, 'Enter an amount.');
export const signedAmount = z.number().int().min(-MAX_AMOUNT).max(MAX_AMOUNT);

/** Positive milli-units. */
export const qty = z
  .number()
  .int('Quantity must be whole milli-units.')
  .min(1, 'Enter a quantity.')
  .max(MAX_QTY, 'Quantity is too large.');
export const signedQty = z.number().int().min(-MAX_QTY).max(MAX_QTY);

export const saleLineInput = z.object({ itemId: id, qty, unitPrice: amount });
export const purchaseLineInput = z.object({ itemId: id, qty, unitCost: amount });

export const saleInput = z.object({
  date: isoDate,
  partyId: id.nullable().default(null),
  lines: z.array(saleLineInput).min(1, 'Add at least one item.').max(200, 'Too many lines.'),
  paid: amount,
  accountId: id,
  note,
});

export const purchaseInput = z.object({
  date: isoDate,
  partyId: id.nullable().default(null),
  lines: z.array(purchaseLineInput).min(1, 'Add at least one item.').max(200, 'Too many lines.'),
  paid: amount,
  accountId: id,
  note,
});

export const paymentInput = z.object({
  date: isoDate,
  partyId: id,
  direction: z.enum(['in', 'out']),
  amount: positiveAmount,
  accountId: id,
  note,
});

export const returnInput = z.object({
  date: isoDate,
  kind: z.enum(['sale', 'purchase']),
  partyId: id.nullable().default(null),
  itemId: id,
  qty,
  /** null = work it out from the item's price (sale) or cost (purchase). */
  amount: amount.nullable().default(null),
  reason: z.enum(RETURN_REASONS),
  bin: z.enum(BINS),
  accountId: id,
  note,
});

export const expenseInput = z.object({
  date: isoDate,
  purpose: label,
  amount: positiveAmount,
  accountId: id,
  note,
});

export const drawingInput = z.object({
  date: isoDate,
  who: label,
  amount: positiveAmount,
  accountId: id,
  note,
});

export const transferInput = z.object({
  date: isoDate,
  fromAccountId: id,
  toAccountId: id,
  amount: positiveAmount,
  note,
});

export const stockAdjustInput = z.object({
  date: isoDate,
  itemId: id,
  bin: z.enum(BINS),
  qty: signedQty,
  unitCost: amount.nullable().default(null),
  reason: z.enum(ADJUST_REASONS),
  note,
});

export const accountOpeningInput = z.object({
  date: isoDate,
  accountId: id,
  amount: signedAmount,
  note,
});

export const partyOpeningInput = z.object({
  date: isoDate,
  partyId: id,
  amount: signedAmount,
  note,
});

export const voidInput = z.object({
  date: isoDate,
  targetId: id,
  reason: z.string().trim().max(200, 'Reason is too long.').default(''),
});

// Master data ---------------------------------------------------------------

export const itemInput = z.object({
  name: label,
  category: z
    .string()
    .trim()
    .max(50, 'Too long.')
    .default('')
    .transform((s) => (s === '' ? 'Other' : s)),
  origin: z.string().trim().max(50, 'Too long.').default(''),
  unit: z.enum(UNITS).default('pcs'),
  minQty: z
    .number()
    .int()
    .min(0)
    .max(MAX_QTY)
    .default(0)
    .transform((n) => n as Milli),
  sellPrice: amount.default(0).transform((n) => n as Paisa),
  active: z.boolean().default(true),
});

export const partyInput = z.object({
  name: label,
  type: z.enum(PARTY_TYPES),
  phone: z.string().trim().max(30, 'Too long.').default(''),
  note: z.string().trim().max(200, 'Too long.').default(''),
  active: z.boolean().default(true),
});

export const accountInput = z.object({
  name: label,
  kind: z.enum(ACCOUNT_KINDS),
  active: z.boolean().default(true),
});

export type SaleInput = z.input<typeof saleInput>;
export type PurchaseInput = z.input<typeof purchaseInput>;
export type PaymentInput = z.input<typeof paymentInput>;
export type ReturnInput = z.input<typeof returnInput>;
export type ExpenseInput = z.input<typeof expenseInput>;
export type DrawingInput = z.input<typeof drawingInput>;
export type TransferInput = z.input<typeof transferInput>;
export type StockAdjustInput = z.input<typeof stockAdjustInput>;
export type AccountOpeningInput = z.input<typeof accountOpeningInput>;
export type PartyOpeningInput = z.input<typeof partyOpeningInput>;
export type VoidInput = z.input<typeof voidInput>;
export type ItemInput = z.input<typeof itemInput>;
export type PartyInput = z.input<typeof partyInput>;
export type AccountInput = z.input<typeof accountInput>;
